import {
  Filesystem,
  Directory,
  DeleteFileOptions,
  ReadFileOptions,
  ReadFileResult,
  StatOptions,
  Encoding
} from "@capacitor/filesystem";
import {
  Http,
  HttpDownloadFileResult,
  HttpHeaders,
} from "@capacitor-community/http";
import { FileOpener } from "@ionic-native/file-opener";
import {
  DocumentViewer,
  DocumentViewerOptions,
} from "@ionic-native/document-viewer";
import { StatusBar } from "@capacitor/status-bar";
import { ITenant } from "../models/tenant";
import { ICompany } from "../models/company";

const defaultDirectory = Directory.Documents;

const Download = {
  meetingPdf: (meetingId: number, contentId: number) =>
    downloadFile(
      `${process.env.REACT_APP_DOMAIN}${process.env.REACT_APP_API_URL}/content/meetings/${meetingId}/download/${contentId}?format=pdf`,
      `meetings/${meetingId}/${contentId}.pdf`,
      window.localStorage.getItem("jwt_p") ?? ""
    ),
  boardPdf: (boardId: number, contentId: number) =>
    downloadFile(
      `${process.env.REACT_APP_DOMAIN}${process.env.REACT_APP_API_URL}/content/boards/${boardId}/download/${contentId}?format=pdf`,
      `boards/${boardId}/${contentId}.pdf`,
      window.localStorage.getItem("jwt_p") ?? ""
    ),
  tenantLogo: (tenant:ITenant, fileName: "logo.png" | "logo_sidebar.png") => downloadFile(`${process.env.REACT_APP_DOMAIN}/img/tenants${tenant.path}/${fileName}`, `img/tenants${tenant.path}/${fileName}`),
  companyLogo: (company:ICompany, fileName: "logo.png" | "background.png") => downloadFile(`${process.env.REACT_APP_DOMAIN}/img/companies/${company.name}/${fileName}`, `img/companies/${company.name}/${fileName}`),
  companyThemeCss: (fileName: "themeCompany.json") => downloadFile(`${process.env.REACT_APP_DOMAIN}/themesPlayer/${fileName}`, `img/companies/${fileName}`),
  publicFile: (url: string, fileName: string) => downloadFile(url, fileName),
  tenantCss: (tenant:ITenant, fileName: "theme.css") => downloadFile(`${process.env.REACT_APP_DOMAIN}/themesPlayer/${tenant.theme}/${fileName}`, `img/tenants${tenant.path}/${fileName}`),
};

const Read = {
  meetingPdf: (meetingId: number, contentId: number) =>
    readFile(`meetings/${meetingId}/${contentId}.pdf`),
  file: (fileName: string) => readFile(fileName),
  textFile: (fileName: string) => readTextFile(fileName),
};

const Open = {
  meetingPdf: (
    meetingId: number,
    contentId: number,
    type:
      | "file-opener"
      | "file-opener-dialog"
      | "document-viewer" = "file-opener",
    OnSuccess?: () => void,
    OnError?: () => void
  ) =>
    openFile(
      `meetings/${meetingId}/${contentId}.pdf`,
      "application/pdf",
      type,
      OnSuccess,
      OnError
    ),
  boardPdf: (
    boardId: number,
    contentId: number,
    type:
      | "file-opener"
      | "file-opener-dialog"
      | "document-viewer" = "file-opener",
    OnSuccess?: () => void,
    OnError?: () => void
  ) =>
    openFile(
      `boards/${boardId}/${contentId}.pdf`,
      "application/pdf",
      type,
      OnSuccess,
      OnError
    ),
};

const GetLocalUrl = {
  tenantCssTheme: (tenant:ITenant, fileName: string) => getFileUrl(`img/tenants${tenant.path}/${fileName}`),
  tenantLogo: (tenant:ITenant, fileName: string) => getFileUrl(`img/tenants${tenant.path}/${fileName}`),
  companyLogo: (company:ICompany, fileName: string) => getFileUrl(`img/companies/${company.name}/${fileName}`),
  companyThemeCss: (fileName: string) => getFileUrl(`img/companies/${fileName}`),
};

const Write = {}; //todo scrittura su filesystem

// const Delete = {
//   file: (fileName: string) => deleteFile(fileName),
// };

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  Download,
  Read,
  Write,
  Open,
  GetLocalUrl,
};

const downloadFile = async (
  downloadUrl: string,
  fileName: string,
  token?: string
) => {
  await deleteFile(fileName);
  const authHeader: HttpHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  const options = {
    url: downloadUrl,
    filePath: fileName,
    fileDirectory: defaultDirectory,
    headers: authHeader,
  };

  const response: HttpDownloadFileResult = await Http.downloadFile(options);
  return response;
};

const deleteFile = async (fileName: string) => {
  try {
    const options: DeleteFileOptions = {
      path: fileName,
      directory: defaultDirectory,
    };
    await Filesystem.deleteFile(options);
  } catch (error) {}
};

const readFile = async (fileName: string) => {
  try {
    const options: ReadFileOptions = {
      path: fileName,
      directory: defaultDirectory,
    }; //encoding:  Encoding.UTF8 ???
    const file: ReadFileResult = await Filesystem.readFile(options);
    return file.data;
  } catch (error) {}
};

const readTextFile = async (fileName: string) => {
  try {
    const options: ReadFileOptions = {
      path: fileName,
      directory: defaultDirectory,
      encoding: Encoding.UTF8
    };
    const file: ReadFileResult = await Filesystem.readFile(options);
    return file.data;
  } catch (error) {}
};

const getFileUrl = async (fileName: string) => {
  try {
    const statOptions: StatOptions = {
      path: fileName,
      directory: defaultDirectory,
    }; //encoding:  Encoding.UTF8 ???
    const fileStat = await Filesystem.stat(statOptions);
    return fileStat.uri;
  } catch (error) {
    throw error;
  }
};

const openFile = async (
  fileName: string,
  MIMEType: string,
  type: "file-opener" | "file-opener-dialog" | "document-viewer",
  OnSuccess?: () => void,
  OnError?: () => void
) => {
  const url = await getFileUrl(fileName);
  switch (type) {
    case "file-opener":
      FileOpener.open(url, MIMEType).then(OnSuccess).catch(OnError);
      break;
    case "file-opener-dialog":
      FileOpener.showOpenWithDialog(url, MIMEType)
        .then(OnSuccess)
        .catch(OnError);
      break;
    case "document-viewer":
      await StatusBar.show();
      const customOnSuccess = async () => {
        await StatusBar.hide();
        if (OnSuccess) OnSuccess();
      };
      const customOnError = async () => {
        await StatusBar.hide();
        if (OnError) OnError();
      };
      const options: DocumentViewerOptions = {
        title: "Documento allegato",
        documentView: { closeLabel: "Chiudi" },
        navigationView: { closeLabel: "Chiudi" },
        search: { enabled: true },
      };
      DocumentViewer.viewDocument(
        url,
        MIMEType,
        options,
        undefined,
        customOnSuccess,
        customOnError,
        customOnError
      );
      break;
    default:
      return FileOpener.open(url, MIMEType).then(OnSuccess).catch(OnError);
  }
};
